/**
 * German translations for QuizNudge.
 * This is the primary locale for the DACH market.
 */
export const de = {
  // Navigation
  nav: {
    quiz: "Quiz",
    recommendations: "Empfehlungen",
    products: "Produkte",
    login: "Anmelden",
    signUp: "Registrieren",
    logout: "Abmelden",
  },

  // Footer
  footer: {
    copyright: "© {year} QuizNudge. Alle Rechte vorbehalten.",
    privacyPolicy: "Datenschutz",
    termsOfService: "AGB",
    help: "Hilfe",
  },

  // Error States
  error: {
    title: "Fehler",
    retry: "Erneut versuchen",
    csrfInvalid: "Ungültiges oder fehlendes Sicherheits-Token",
    genericError: "Ein Fehler ist aufgetreten",
    networkError: "Netzwerkfehler. Bitte prüfen Sie Ihre Verbindung.",
    notFound: "Seite nicht gefunden",
    unexpectedError:
      "Es tut uns leid. Die Anwendung hat einen unerwarteten Fehler festgestellt.",
    reloadPage: "Seite neu laden",
    goHome: "Zur Startseite",
    tryContinue: "Trotzdem fortfahren",
    details: "Fehlerdetails:",
    componentStack: "Komponenten-Stack",
  },

  // Loading States
  loading: {
    default: "Wird geladen...",
    products: "Produkte werden geladen...",
    recommendations: "Empfehlungen werden geladen...",
  },

  // Common / Shared
  common: {
    viewDetails: "Details ansehen",
    back: "Zurück",
    next: "Weiter",
    previous: "Zurück",
    clearAll: "Alle zurücksetzen",
    filters: "Filter",
    sortBy: "Sortieren nach",
    bestMatch: "Beste Übereinstimmung",
    categories: "Kategorien",
    priceRange: "Preisbereich",
    minPrice: "Mindestpreis",
    maxPrice: "Höchstpreis",
    availableRange: "Verfügbarer Bereich:",
    noResults: "Keine Ergebnisse gefunden",
    priceLowToHigh: "Preis: Aufsteigend",
    priceHighToLow: "Preis: Absteigend",
    nameAToZ: "Name: A bis Z",
    activeFilters: "Aktive Filter",
  },

  // Quiz
  quiz: {
    questionOf: "Frage {current} von {total}",
    submitting: "Wird gesendet...",
    allAnswered: "Alle Fragen beantwortet!",
    progressLabel: "Quiz-Fortschritt: {percentage}% abgeschlossen",
    completeQuiz: "Quiz abschließen",
    notAvailable: "Aktuell ist kein Quiz verfügbar",
    notAvailableMessage: "Bitte versuchen Sie es später erneut.",
    validation: {
      questionRequired: "Diese Frage muss beantwortet werden.",
      fieldRequired: "Dieses Feld ist erforderlich.",
      selectOption: "Bitte wählen Sie eine Option aus.",
      selectAtLeastOne: "Bitte wählen Sie mindestens eine Option aus.",
      selectAtLeast: "Bitte wählen Sie mindestens {min} Optionen aus.",
      selectNoMoreThan: "Bitte wählen Sie maximal {max} Optionen aus.",
      minLength: "Mindestens {min} Zeichen erforderlich.",
      maxLength: "Maximal {max} Zeichen erlaubt.",
      invalidFormat: "Ungültiges Format.",
      invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
      looksGood: "Sieht gut aus!",
      selectionConfirmed: "Auswahl bestätigt.",
      optionsSelected: "{count} Optionen ausgewählt.",
      maxReached:
        "Maximale Auswahl erreicht. Bitte entfernen Sie eine Option, um eine andere zu wählen.",
      questionOptional: "Diese Frage ist optional.",
      minCharsRequired: "Mindestens {min} Zeichen erforderlich.",
      moreCharsNeeded: "Noch {count} Zeichen benötigt.",
    },
  },

  // Products
  products: {
    catalog: "Produktkatalog",
    browseDescription: "Entdecken Sie unser gesamtes Sortiment",
    showing: "{from}–{to} von {total} Produkten",
    description: "Beschreibung",
    specifications: "Spezifikationen",
    addToCart: "In den Warenkorb",
    breadcrumbHome: "Startseite",
    breadcrumbProducts: "Produkte",
    category: "Kategorie",
    recentlyViewed: "Zuletzt angesehen",
  },

  // Recommendations
  recommendations: {
    title: "Ihre persönlichen Empfehlungen",
    subtitle:
      "Basierend auf Ihren Antworten haben wir {count} passende Produkte gefunden.",
    backToQuiz: "← Zurück zum Quiz",
    whyThisProduct: "Warum dieses Produkt?",
    matchScore: "Übereinstimmung",
    confidence: "Konfidenz:",
    keyMatches: "Wichtigste Kriterien",
    detailedReasoning: "Detaillierte Begründung",
    whyThisMatch: "Warum dieses Ergebnis?",
    matchLabels: {
      excellent: "Ausgezeichnete Übereinstimmung",
      veryGood: "Sehr gute Übereinstimmung",
      good: "Gute Übereinstimmung",
      fair: "Ordentliche Übereinstimmung",
      moderate: "Mäßige Übereinstimmung",
      poor: "Geringe Übereinstimmung",
    },
    matchBadge: "{score}% Übereinstimmung",
    criteriaLabels: {
      priceRange: "Preisbereich",
      category: "Kategorie",
      brandPreferences: "Markenpräferenzen",
    },
    noSession:
      "Bitte schließen Sie zuerst das Quiz ab, um personalisierte Empfehlungen zu erhalten.",
    startQuiz: "Quiz starten",
    analyzing: "Ihre Quiz-Antworten werden analysiert...",
    startNewQuiz: "Neues Quiz starten",
    noResults: "Keine Empfehlungen verfügbar",
    noResultsText:
      "Basierend auf Ihren Antworten konnten wir leider keine passenden Produkte finden. Bitte versuchen Sie es mit einem neuen Quiz.",
    detail: {
      recommendedForYou: "Für Sie empfohlen",
      basedOnQuiz: "Basierend auf Ihren Quiz-Antworten",
      whyWeRecommend: "Warum wir dieses Produkt empfehlen",
      viewFullDetails: "Alle Details anzeigen",
      otherRecommendations: "Weitere Empfehlungen",
    },
  },

  // Auth
  auth: {
    validation: {
      emailRequired: "E-Mail-Adresse ist erforderlich",
      emailInvalid: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
      passwordRequired: "Passwort ist erforderlich",
      passwordMinLength6: "Passwort muss mindestens 6 Zeichen lang sein",
      passwordMinLength8: "Passwort muss mindestens 8 Zeichen lang sein",
      passwordComplexity:
        "Passwort muss Groß-, Kleinbuchstaben und Zahlen enthalten",
      passwordsMismatch: "Passwörter stimmen nicht überein",
      acceptTermsRequired: "Sie müssen die AGB akzeptieren",
    },
    login: {
      title: "Willkommen zurück",
      subtitle: "Melden Sie sich an, um fortzufahren",
      emailLabel: "E-Mail-Adresse",
      emailPlaceholder: "ihre@email.de",
      passwordLabel: "Passwort",
      rememberMe: "Angemeldet bleiben",
      forgotPassword: "Passwort vergessen?",
      submitButton: "Anmelden",
      signingIn: "Anmeldung läuft...",
      noAccount: "Noch kein Konto?",
      signUpLink: "Registrieren",
    },
    register: {
      title: "Konto erstellen",
      subtitle: "Registrieren Sie sich für QuizNudge",
      emailLabel: "E-Mail-Adresse",
      emailPlaceholder: "ihre@email.de",
      passwordLabel: "Passwort",
      passwordHint:
        "Mindestens 8 Zeichen mit Groß-, Kleinbuchstaben und Zahlen",
      confirmPasswordLabel: "Passwort bestätigen",
      acceptTerms: "Ich akzeptiere die",
      termsLink: "AGB",
      andText: "und",
      privacyLink: "Datenschutzerklärung",
      submitButton: "Konto erstellen",
      creatingAccount: "Konto wird erstellt...",
      hasAccount: "Bereits ein Konto?",
      signInLink: "Anmelden",
    },
  },

  // Categories (slug → German label mapping)
  categories: {
    electronics: "Elektronik",
    fashion: "Mode",
    home: "Haus & Garten",
    beauty: "Schönheit & Pflege",
    food: "Lebensmittel",
    wine: "Wein",
    pets: "Tierbedarf",
    supplements: "Nahrungsergänzung",
  },
} as const;

export type TranslationKeys = typeof de;
